// QuotasSection.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../context/authContext';
import {
  CreditCard,
  FileText,
  History,
  Mail,
  Plus,
  X,
} from 'lucide-react';
import PaymentSection from './AuxQuotas/PaymentsSection';
import QuotaItem from './AuxQuotas/QuotaItem';
import Swal from 'sweetalert2';
import { ActionLoader } from '../../loader';
import { createPayment } from '../../../services/paymentService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

import { sendReminderGenericEmail, updateQuotaAmount } from '../../../services/quotasService';

const QuotasSection = ({ selectedSocio, quotas, onViewPaymentHistory, onViewAddPayment, onClose, reloadQuotas }) => {
  const { user } = useAuth();
  dayjs.extend(utc);
  dayjs.extend(timezone);

  // Constante para el orden de los meses
  const MESES_ORDEN = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
  ];

  // Función para obtener el índice de orden de una cuota
  const obtenerIndiceOrden = useCallback((cuota) => {
    const periodo = cuota.period.toLowerCase().trim();
    
    // Semestres: ordenar antes que los meses
    if (periodo.includes("semestre")) {
      return periodo.includes("1") ? -2 : -1; // Semestre 1 antes que Semestre 2
    }
    
    // Anual: ordenar al final
    if (periodo.includes("anual")) {
      return 12; // Después de diciembre
    }
    
    // Mensual: usar índice del mes
    const indiceMes = MESES_ORDEN.findIndex(mes => mes === periodo);
    return indiceMes >= 0 ? indiceMes : 13; // Si no encuentra el mes, al final
  }, []);

  // Función para ordenar cuotas por período
  const ordenarCuotasPorPeriodo = useCallback((cuotas) => {
    return [...cuotas].sort((a, b) => {
      const indiceA = obtenerIndiceOrden(a);
      const indiceB = obtenerIndiceOrden(b);
      return indiceA - indiceB;
    });
  }, [obtenerIndiceOrden]);

  // Función para filtrar cuotas hasta la fecha actual
  const filtrarCuotasHastaFechaActual = useCallback((cuotas) => {
    const ahora = new Date();
    const mesActual = ahora.getMonth(); 
    const añoActual = ahora.getFullYear();

    return cuotas.filter((cuota) => {
      const { period, year } = cuota;
      const añoCuota = parseInt(year);

      if (añoCuota > añoActual) return false;
      if (añoCuota < añoActual) return true;

      // Año igual al actual
      const periodo = period.toLowerCase().trim();

      if (periodo.includes("semestre")) {
        const semestreActual = mesActual <= 5 ? 1 : 2;
        const semestreCuota = periodo.includes("1") ? 1 : 2;
        return semestreCuota <= semestreActual;
      }

      if (periodo.includes("anual")) {
        return true; // mostrar durante todo el año
      }

      // Mensual
      const mesCuota = MESES_ORDEN.findIndex((mes) => mes === periodo);
      return mesCuota >= 0 && mesCuota <= mesActual;
    });
  }, []);

  // Memoizar años disponibles
  const availableYears = useMemo(() => 
    [...new Set(quotas.map((q) => q.year))].sort((a, b) => b - a),
    [quotas]
  );
  
  const [selectedYear, setSelectedYear] = useState(() => availableYears[0] || null);
  const [selectedQuotas, setSelectedQuotas] = useState(new Map());
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [isCalculated, setIsCalculated] = useState(false);
  const [lastCalculatedQuotas, setLastCalculatedQuotas] = useState(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoizar funciones de formateo
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'No pagada';

    const fechaFormateada = dayjs.tz(dateString, 'America/Santiago') // Lo toma directo como Chile
      .format('D [de] MMMM [de] YYYY');
    return fechaFormateada;
  };



  // Memoizar cuotas filtradas por año
  const quotasByYear = useMemo(() => 
    selectedYear 
      ? quotas.filter((q) => q.year === selectedYear)
      : quotas,
    [quotas, selectedYear]
  );

  // CAMBIO PRINCIPAL: Aplicar filtro de fecha y ordenamiento a las cuotas ya filtradas por año
  const filteredQuotas = useMemo(() => {
    const cuotasFiltradas = filtrarCuotasHastaFechaActual(quotasByYear);
    return ordenarCuotasPorPeriodo(cuotasFiltradas);
  }, [quotasByYear, filtrarCuotasHastaFechaActual, ordenarCuotasPorPeriodo]);

  // Memoizar cuotas pendientes (ya filtradas por fecha)
  const pendingQuotas = useMemo(() => 
    filteredQuotas.filter(q => q.status === 'pending'),
    [filteredQuotas]
  );

  // Memoizar estadísticas del resumen (ya con filtro de fecha aplicado)
  const quotaStats = useMemo(() => {
    const paidQuotas = filteredQuotas.filter(q => q.status === 'paid').length;
    const pendingCount = filteredQuotas.filter(q => q.status === 'pending').length;
    const totalAmount = filteredQuotas.reduce((sum, q) => sum + parseFloat(q.amount), 0);
    
    return {
      total: filteredQuotas.length,
      paid: paidQuotas,
      pending: pendingCount,
      totalAmount
    };
  }, [filteredQuotas]);

  // Función auxiliar para comparar Maps
  const areMapsEqual = useCallback((map1, map2) => {
    if (map1.size !== map2.size) return false;
    for (let [key] of map1) {
      if (!map2.has(key)) return false;
    }
    return true;
  }, []);

  // Funciones auxiliares para trabajar con selectedQuotas
  const getSelectedQuotasArray = useCallback(() => Array.from(selectedQuotas.values()), [selectedQuotas]);
  const getSelectedQuotasIds = useCallback(() => Array.from(selectedQuotas.keys()), [selectedQuotas]);

  // Función para resetear selecciones
  const resetSelections = useCallback(() => {
    setSelectedQuotas(new Map());
    setIsCalculated(false);
    setCalculatedTotal(0);
    setLastCalculatedQuotas(new Map());
  }, []);

  // Seleccionar todas las cuotas pendientes
  const handleSelectAll = useCallback(() => {
    const allPendingMap = new Map(pendingQuotas.map(q => [q.id, q]));
    setSelectedQuotas(allPendingMap);
    
    // Resetear el estado de cálculo ya que cambiaron las selecciones
    if (!areMapsEqual(allPendingMap, lastCalculatedQuotas)) {
      setIsCalculated(false);
      setCalculatedTotal(0);
    }
  }, [pendingQuotas, areMapsEqual, lastCalculatedQuotas]);

  // Deseleccionar todas las cuotas
  const handleDeselectAll = useCallback(() => {
    resetSelections();
  }, [resetSelections]);

  // Manejar selección individual de cuotas
  const handleQuotaSelection = useCallback((quotaId) => {
    setSelectedQuotas(prev => {
      const newSelectedQuotas = new Map(prev);
      
      if (newSelectedQuotas.has(quotaId)) {
        newSelectedQuotas.delete(quotaId);
      } else {
        const quotaObject = filteredQuotas.find(q => q.id === quotaId);
        if (quotaObject) {
          newSelectedQuotas.set(quotaId, quotaObject);
        }
      }
      
      // Si cambian las cuotas seleccionadas, resetear el estado de cálculo
      if (!areMapsEqual(newSelectedQuotas, lastCalculatedQuotas)) {
        setIsCalculated(false);
        setCalculatedTotal(0);
      }
      
      return newSelectedQuotas;
    });
  }, [areMapsEqual, lastCalculatedQuotas, filteredQuotas]);

  // Calcular total de pago
  const handleCalculatePayment = useCallback(() => {
    const selectedQuotasArray = Array.from(selectedQuotas.values());
    const total = selectedQuotasArray.reduce((sum, q) => sum + parseFloat(q.amount), 0);
    setCalculatedTotal(total);
    setIsCalculated(true);
    setLastCalculatedQuotas(new Map(selectedQuotas));
  }, [selectedQuotas]);

  const handlePayment = useCallback(async () => {
    const selectedQuotasIds = getSelectedQuotasIds();

    if (user.role === 'admin') {
      await Swal.fire('Acción no permitida', 'Los administradores no pueden realizar pagos.', 'info');
      return;
    }

    // if (user.rut != "76333333-7") {
    //   await Swal.fire('Por el momento no esta disponible el pago por cuotas', '','info');
    //   return;
    // }

    /*
    await Swal.fire({
      title: 'Proximamente podras pagar tus cuotas mediante WebPay',
      text: 'Por ahora, por favor contacta a tu administrador para realizar el pago.',
      icon: 'info',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#0488AF',
      allowOutsideClick: false,
    });
    */
   
    const confirmation = await Swal.fire({
      title: 'Confirmar Pago',
      text: `¿Estás seguro de que deseas pagar ${formatCurrency(calculatedTotal)} por las cuotas seleccionadas?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Pagar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#C42251',
    });

    if (!confirmation.isConfirmed) return;
    let response
  try {
    setIsSubmitting(true);
    response = await createPayment({
      selectedQuotas: selectedQuotasIds,
      sessionId: user.rut,
      amount: calculatedTotal,
    });
    console.log("Respuesta de Transbank:", response);

    if (response?.token?.trim() && response?.url?.trim()) {
      await Swal.fire({
        title: 'Redirigiendo a WebPay',
        text: 'Te estamos llevando a la página de pago segura de Transbank.',
        icon: 'info',
        timer: 2000,
        showConfirmButton: false,
        allowOutsideClick: false,
      });
      window.location.href = `${response.url}?token_ws=${response.token}`;
      resetSelections();
    } else {
      throw new Error('Respuesta inválida del servidor');
    }
    } catch (err) {
        let errorMessage = 'Ocurrió un problema al intentar procesar el pago.';
        if (response?.statusCode === 400) {
          errorMessage = err.response.data.message || 'Datos de pago inválidos.';
        } else if (response?.statusCode === 401) {
          errorMessage = 'No tienes autorización para realizar este pago.';
        } else if (response?.statusCode >= 500) {
          errorMessage = 'Error del servidor. Por favor, intenta más tarde.';
        } else if (response.statusCode == 403) {
          errorMessage = 'Los pagos están deshabilitados temporalmente ';
        }

        Swal.fire('Error en el Pago', errorMessage, 'error')

    } finally {
        setIsSubmitting(false);
    }
    
  }, [calculatedTotal, getSelectedQuotasIds, user.rut, user.role, resetSelections, isCalculated, formatCurrency]);



  // Manejar cambio de año
  const handleYearChange = useCallback((e) => {
    const year = parseInt(e.target.value) || null;
    setSelectedYear(year);
  }, []);

  // Actualizar monto de cuota
  const updateAmount = useCallback(async (cuotaId, newAmount) => {
    setIsSubmitting(true);
    try {
      const response = await updateQuotaAmount(cuotaId, newAmount);
      if (response.error) {
        Swal.fire({
          title: 'Error',
          text: response.message,
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
        return;
      }
      Swal.fire({
        title: 'Éxito',
        text: `Monto actualizado a ${formatCurrency(newAmount)}`,
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
      // Actualizar la cuota en el estado
      reloadQuotas();
    } catch (error) {
      console.error('Error updating amount:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al actualizar el monto. Por favor, inténtelo de nuevo.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formatCurrency]);

  // Enviar email de recordatorio
  const sendEmail = useCallback(async () => {
    const emailRecipient = selectedSocio.contact_email || selectedSocio.business_name;
    const confirmation = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${emailRecipient}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
    });
    
    if (!confirmation.isConfirmed) return;
    try {
      setIsSubmitting(true);
      const response = await sendReminderGenericEmail(selectedSocio.rut);
      if (response.success === false) {
        Swal.fire({
          title: 'Error',
          text: response.message,
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      } else {
        Swal.fire({
          title: 'Éxito',
          text: 'Recordatorio enviado correctamente.',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      }
    } catch (error) {
      console.error('Error sending reminder email:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al enviar el recordatorio. Por favor, inténtelo de nuevo.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedSocio.rut]);

  // Resetear selecciones cuando cambie el año
  useEffect(() => {
    resetSelections();
  }, [selectedYear, resetSelections]);

  // Actualizar selectedYear cuando cambien los availableYears
  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  if (!selectedSocio) return null;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-r from-[#0488AF] to-[#C42251] rounded-xl">
            <FileText className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">{selectedSocio.business_name}</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Nombre Comercial:</span> {selectedSocio.trade_name}</p>
              <p><span className="font-medium">RUT:</span> {selectedSocio.rut}</p>
              <p><span className="font-medium">Correo:</span> {selectedSocio.contact_email}</p>
              <p><span className="font-medium">Teléfono:</span> {selectedSocio.mobile_phone}</p>
              <p className="font-extrabold underline">
                <span className="font-extrabold">Frecuencia de pago:</span> {selectedSocio.periodicity}
              </p>
              <p>
                <span className="font-extrabold">Monto:</span> {selectedSocio.amount} UF
              </p>
            </div>
          </div>
        </div>
        {user.role === 'admin' && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Acciones y Filtro */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="text-[#0488AF]" size={20} />
          <h4 className="text-lg font-semibold text-gray-800">Historial de Cuotas</h4>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-6">
          {user.role === 'admin' && (
            <>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                onClick={onViewPaymentHistory} 
              >
                <History size={16} />
                Historial de Pagos
              </button>
              <button 
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                onClick={sendEmail}
              >
                <Mail size={16} />
                Enviar Recordatorio
              </button>
              <button 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${calculatedTotal === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors`}
                onClick={() => onViewAddPayment(getSelectedQuotasArray(), calculatedTotal)}
                disabled={calculatedTotal === 0}
              >
                <Plus size={16} />
                Pago Manual
              </button>
            </>
          )}
          {user.role === 'socio' && (
            <button
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={onViewPaymentHistory}
            >
              <History size={16} />
              Historial de Pagos
            </button>
          )}
          {/* Selector de Año */}
          <select
            value={selectedYear || ""}
            onChange={handleYearChange}
            className="ml-auto px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option value="">Todos los años</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Sección de pago */}
        {pendingQuotas.length > 0 && (
          <PaymentSection
            selectedQuotas={selectedQuotas}
            pendingQuotas={pendingQuotas}
            isCalculated={isCalculated}
            calculatedTotal={calculatedTotal}
            areSetsEqual={areMapsEqual}
            lastCalculatedQuotas={lastCalculatedQuotas}
            handleCalculatePayment={handleCalculatePayment}
            handlePayment={handlePayment}
            handleSelectAll={handleSelectAll}
            handleDeselectAll={handleDeselectAll}
            formatCurrency={formatCurrency}
            userRole={user.role}
            getSelectedQuotasArray={getSelectedQuotasArray}
            getSelectedQuotasIds={getSelectedQuotasIds}
          />
        )}

        {/* Lista de Cuotas */}
        <div className="grid gap-4">
          {filteredQuotas.map((cuota) => (
            <QuotaItem
              key={cuota.id}
              cuota={cuota}
              isSelected={selectedQuotas.has(cuota.id)}
              onSelection={handleQuotaSelection}
              userRole={user.role}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              onUpdateAmount={updateAmount}
              onQuotaPaymentRemoved={reloadQuotas}
              empresaNombre={selectedSocio?.business_name || selectedSocio?.trade_name || 'la empresa'}
            />
          ))}
        </div>

        {/* Resumen */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-rose-50 rounded-xl border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-[#0488AF]">{quotaStats.total}</div>
              <div className="text-sm text-gray-600">Total Cuotas</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{quotaStats.paid}</div>
              <div className="text-sm text-gray-600">Pagadas</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{quotaStats.pending}</div>
              <div className="text-sm text-gray-600">Pendientes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#C42251]">
                {formatCurrency(quotaStats.totalAmount)}
              </div>
              <div className="text-sm text-gray-600">Total Facturado</div>
            </div>
          </div>
        </div>
      </div>
      
      <ActionLoader open={isSubmitting} onClose={() => setIsSubmitting(false)} />
    </div>
  );
};

export default QuotasSection;